import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { BookOpen, ClipboardCheck, ExternalLink, FileSearch, FileText, FolderOpen, History, ListChecks } from "lucide-react";
import { api } from "../api/client";
import { useResource } from "../api/resource";
import type { SectionSources, SourceSection } from "../api/types";
import { roleLabel, typeLabel } from "../components/documents";
import { SidePanel } from "../components/overlay";
import { EmptyState, ErrorState, PageHeader, SkeletonBlock, StatusBadge } from "../components/ui";
import { keys, useDossier } from "../dossier";
import { w } from "../i18n";
import { readinessLabel, readinessStatus } from "../status";
import { RequirementRow } from "./readiness";
import { AgentInputs, DraftTab, HistoryTab, ValidationTab } from "../components/drafting";
import { ComparisonTab } from "../components/comparison";

type Tab = "inputs" | "sources" | "draft" | "comparison" | "validation" | "history";
const TABS: { key: Tab; icon: ReactNode }[] = [
  { key: "draft", icon: <FileText size={16} /> },
  { key: "inputs", icon: <ListChecks size={16} /> },
  { key: "sources", icon: <FolderOpen size={16} /> },
  { key: "comparison", icon: <BookOpen size={16} /> },
  { key: "validation", icon: <ClipboardCheck size={16} /> },
  { key: "history", icon: <History size={16} /> },
];

function SourceViewer({ source, onClose }: { source: { documentId: string; name: string; page?: number; section?: SourceSection }; onClose: () => void }) {
  return <SidePanel wide title={source.name} subtitle={source.section ? `${source.section.code} ${source.section.title}${source.page ? ` · ${w("common.pages", { pages: source.page })}` : ""}` : undefined} onClose={onClose}>
    <div className="ws-row"><a className="ws-btn sm" href={api.fileUrl(source.documentId, source.page)} target="_blank" rel="noreferrer">
      <ExternalLink size={14} />{w("panel.openOriginal")}</a></div>
    {source.section && <div className="ws-source-text">{source.section.content}</div>}
    {/* Browsers' PDF viewers honour #page=N; Office files open as a download instead. */}
    {source.name.toLowerCase().endsWith(".pdf") && <iframe className="ws-viewer" title={source.name} src={api.fileUrl(source.documentId, source.page)} />}
  </SidePanel>;
}

function QrtTemplateRow({ template }: { template: SectionSources["qrt_templates"][number] }) {
  const [open, setOpen] = useState(false);
  const facts = useResource(open ? `qrtfacts:${template.document.id}:${template.template_code}` : null,
    () => api.qrtFacts(template.document.id, template.template_code));
  return <li style={{ flexDirection: "column", alignItems: "stretch" }}>
    <div className="ws-row" style={{ justifyContent: "space-between" }}>
      <span><span className="ws-tag code">{template.template_code}</span> {template.title}
        <span className="ws-cell-sub">{template.document.name} · {roleLabel(template.document.period_role)} · {w("section.facts", { count: template.fact_count })}</span></span>
      <span className="ws-row"><StatusBadge status={template.document.status === "ready" ? "ready" : "needs_review"} />
        <button className="ws-btn sm" aria-expanded={open} onClick={() => setOpen(value => !value)}>{open ? w("section.hideFacts") : w("section.showFacts")}</button></span>
    </div>
    {open && facts.loading && <SkeletonBlock lines={3} />}
    {open && facts.error && <ErrorState title={w("common.genericError")} error={facts.error} onRetry={facts.reload} />}
    {open && facts.data && <div className="ws-table-wrap" style={{ maxHeight: 360, marginTop: 8 }}>
      <table className="ws-table"><thead><tr><th>Row</th><th>Col</th><th>{w("docs.col.document")}</th><th className="num">Value</th><th>Source</th></tr></thead>
        <tbody>{facts.data.items.map(fact => <tr key={fact.id}>
          <td className="ws-mono">{fact.row_code || `#${fact.record}`}</td><td className="ws-mono">{fact.column_code}</td>
          <td>{fact.row_label}{fact.column_label && <span className="ws-cell-sub">{fact.column_label}</span>}</td>
          <td className="num">{fact.value ?? fact.value_text} <span className="ws-faint">{fact.unit}{fact.unit_assumed ? "*" : ""}</span></td>
          <td className="ws-small ws-muted">{fact.sheet ? `${fact.sheet}!${fact.cell_ref}` : fact.page ? w("common.pages", { pages: fact.page }) : w("common.notAvailable")}</td>
        </tr>)}</tbody></table></div>}
  </li>;
}

function SourcesTab({ code }: { code: string }) {
  const { dossier } = useDossier();
  const sources = useResource(keys.sources(dossier.id, code), () => api.sectionSources(dossier.id, code));
  const [viewing, setViewing] = useState<{ documentId: string; name: string; page?: number; section?: SourceSection } | null>(null);
  if (sources.loading) return <SkeletonBlock lines={8} />;
  if (sources.error) return <ErrorState title={w("common.genericError")} error={sources.error} onRetry={sources.reload} />;
  const data = sources.data!;
  return <div className="ws-stack">
    <section className="ws-card">
      <div className="ws-card-head"><h2>{w("section.previousReport")}</h2></div>
      {!data.previous_reports.length && <p className="ws-card-pad ws-muted" style={{ margin: 0 }}>{w("section.noPrevious")}</p>}
      {data.previous_reports.map(report => <div key={report.document.id}>
        {report.sections.map(section => <div key={section.id} className="ws-ready-section">
          <div className="ws-ready-head">
            <h3><span className="ws-mono">{section.code}</span>&nbsp; {section.title}</h3>
            <button className="ws-btn sm" onClick={() => setViewing({ documentId: report.document.id, name: report.document.name, page: section.pages[0], section })}>
              <FileSearch size={14} />{w("common.open")}</button>
          </div>
          <p className="ws-small ws-muted" style={{ margin: "2px 0 8px" }}>{report.document.name} · {typeLabel(report.document.document_type)} {report.document.reporting_year}
            {section.pages.length > 0 && ` · ${w("common.pages", { pages: section.pages.join(", ") })}`}</p>
          <div className="ws-source-text" style={{ maxHeight: 220 }}>{section.content || w("common.notAvailable")}</div>
          {section.truncated && <p className="ws-small ws-faint">{w("section.truncated")}</p>}
          {section.tables.map(table => <table key={table.id} className="ws-mini-table"><tbody>{table.rows.slice(0, 12).map((row, i) =>
            <tr key={i}>{row.map((cell, j) => <td key={j}>{cell ?? ""}</td>)}</tr>)}</tbody></table>)}
        </div>)}
      </div>)}
    </section>
    <section className="ws-card">
      <div className="ws-card-head"><h2>{w("section.qrt")}</h2></div>
      {!data.qrt_templates.length && <p className="ws-card-pad ws-muted" style={{ margin: 0 }}>{w("section.noQrt")}</p>}
      <ul className="ws-list">{data.qrt_templates.map(template => <QrtTemplateRow key={template.id} template={template} />)}</ul>
    </section>
    {viewing && <SourceViewer source={viewing} onClose={() => setViewing(null)} />}
  </div>;
}

export function SectionPage() {
  const { code = "" } = useParams();
  const { dossier, readiness } = useDossier();
  const [tab, setTab] = useState<Tab>("draft");
  const section = readiness.data?.chapters.flatMap(chapter => chapter.sections).find(item => item.code === code);

  if (readiness.loading) return <SkeletonBlock lines={8} />;
  if (readiness.error) return <ErrorState title={w("ready.loadError")} error={readiness.error} onRetry={readiness.reload} />;
  if (!section) return <EmptyState title={code} action={<Link className="ws-btn" to={`/dossiers/${dossier.id}/readiness`}>{w("nav.readiness")}</Link>} />;

  const required = section.requirements.filter(requirement => requirement.required).length;
  const ready = section.requirements.filter(requirement => requirement.state === "satisfied").length;

  return <>
    <PageHeader eyebrow={`${w("nav.chapter", { code: code.split(".")[0] })}`} title={<><span className="ws-mono" style={{ fontSize: 20 }}>{section.code}</span> {section.title}</>}
      actions={<StatusBadge status={readinessStatus(section.status)} label={readinessLabel(section.status)} />} />
    <div className="ws-section-workspace">
      <div className="ws-section-main">
        {tab === "inputs" && <div className="ws-stack">
          <section className="ws-card">
            {section.requirements.map(requirement => <div key={requirement.key} style={{ padding: "4px 16px" }}><RequirementRow requirement={requirement} /></div>)}
          </section>
          <AgentInputs code={code} />
        </div>}
        {tab === "sources" && <SourcesTab code={code} />}
        {tab === "draft" && <DraftTab code={code} />}
        {tab === "comparison" && <ComparisonTab code={code} />}
        {tab === "validation" && <ValidationTab code={code} />}
        {tab === "history" && <HistoryTab code={code} />}
      </div>
      <aside className="ws-section-aside" aria-label={w("section.sidebar")}>
        <section className="ws-card ws-section-summary">
          <div>
            <span className="ws-subtitle">{w("section.sidebar")}</span>
            <h2><span className="ws-mono">{section.code}</span> {section.title}</h2>
          </div>
          <StatusBadge status={readinessStatus(section.status)} label={readinessLabel(section.status)} />
          <dl className="ws-dl ws-small">
            <dt>{w("section.requirementsReady")}</dt><dd>{ready}/{section.requirements.length}</dd>
            <dt>{w("section.requirementsRequired")}</dt><dd>{required}</dd>
          </dl>
        </section>
        <nav className="ws-card ws-section-nav" role="tablist" aria-orientation="vertical">
          {TABS.map(item => <button key={item.key} type="button" role="tab" className="ws-section-nav-item" aria-selected={tab === item.key}
            onClick={() => setTab(item.key)}>{item.icon}<span>{w(`section.tab.${item.key}`)}</span></button>)}
        </nav>
      </aside>
    </div>
  </>;
}
